'use client';

import { useEffect, useState, useCallback } from 'react';
import { useToast, Toast, ToastVariant } from '@/contexts/ToastContext';

const VARIANT_STYLES: Record<ToastVariant, { bg: string; border: string; icon: string; progress: string; iconBg: string }> = {
  success: {
    bg: 'bg-[#0F172A]',
    border: 'border-[#C9A961]/40',
    icon: '✓',
    iconBg: 'bg-[#C9A961]/20 text-[#C9A961]',
    progress: 'bg-[#C9A961]',
  },
  error: {
    bg: 'bg-[#0F172A]',
    border: 'border-red-500/40',
    icon: '✕',
    iconBg: 'bg-red-500/20 text-red-400',
    progress: 'bg-red-500',
  },
  warning: {
    bg: 'bg-[#0F172A]',
    border: 'border-amber-500/40',
    icon: '⚠',
    iconBg: 'bg-amber-500/20 text-amber-400',
    progress: 'bg-amber-500',
  },
  info: {
    bg: 'bg-[#0F172A]',
    border: 'border-blue-500/40',
    icon: 'ℹ',
    iconBg: 'bg-blue-500/20 text-blue-400',
    progress: 'bg-blue-500',
  },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const duration = toast.duration ?? 5000;
  const styles = VARIANT_STYLES[toast.variant];

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, [duration]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDismiss]);

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`
        relative overflow-hidden rounded-lg border shadow-2xl
        ${styles.bg} ${styles.border}
        w-80 max-w-[calc(100vw-2rem)]
        transition-all duration-300 ease-out
        ${visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${styles.iconBg}`}>
          {styles.icon}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">{toast.title}</p>
          {toast.message && (
            <p className="mt-0.5 text-xs text-gray-400">{toast.message}</p>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss notification"
          className="flex-shrink-0 rounded p-0.5 text-gray-500 transition-colors hover:text-white focus:outline-none focus:ring-1 focus:ring-[#C9A961]"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 w-full bg-white/10">
        <div
          className={`h-full transition-all ease-linear ${styles.progress}`}
          style={{ width: `${progress}%`, transitionDuration: '50ms' }}
        />
      </div>
    </div>
  );
}

export default function ToastContainer() {
  const { toasts, dismissToast } = useToast();

  return (
    <div
      aria-label="Notifications"
      className="fixed right-4 top-4 z-[9999] flex flex-col gap-3"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}
